import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ArtigoDto, GuiaDto } from '@/types';
import { formatEur } from '@/lib/format';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';

interface GuiasTabProps {
  guias?: GuiaDto[];
  loading: boolean;
  canEdit: boolean;
  onEdit: (guia: GuiaDto) => void;
  onDelete: (guia: GuiaDto) => void;
}

type SortKey = 'data' | 'obra' | 'numero' | 'total';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 20;

function artigoLabel(a: ArtigoDto): string {
  return `${a.quantidade ? `${a.quantidade}× ` : ''}${a.descricao}`;
}

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

export function GuiasTab({ guias, loading, canEdit, onEdit, onDelete }: GuiasTabProps) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>('data');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Set<string>>(new Set());

  // Reset when the underlying dataset changes (e.g. month switch).
  useEffect(() => {
    setPage(1);
    setOpen(new Set());
  }, [guias]);

  const sorted = useMemo(() => {
    const list = [...(guias ?? [])];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sortKey) {
        case 'obra':
          return a.obra.localeCompare(b.obra, undefined, { numeric: true }) * dir;
        case 'numero':
          return a.numero.localeCompare(b.numero, undefined, { numeric: true }) * dir;
        case 'total':
          return (a.total - b.total) * dir;
        case 'data':
        default:
          return (a.data < b.data ? -1 : a.data > b.data ? 1 : 0) * dir;
      }
    });
    return list;
  }, [guias, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'data' || key === 'total' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const toggleOpen = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (loading && !guias) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (!guias || guias.length === 0) {
    return (
      <div className="rounded-2xl border border-conduril-border bg-white py-12 text-center text-sm text-conduril-muted shadow-card">
        {t('dashboard.empty')}
      </div>
    );
  }

  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const Th = ({ label, k, right }: { label: string; k: SortKey; right?: boolean }) => (
    <th className={`px-4 py-3 font-semibold ${right ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-0.5 hover:text-conduril ${sortKey === k ? 'text-conduril' : ''}`}
      >
        {label}
        <span className="text-[10px]">{arrow(k)}</span>
      </button>
    </th>
  );

  const detail = (g: GuiaDto) => (
    <div className="space-y-1.5">
      {g.artigos.map((a, i) => (
        <div key={i} className="flex justify-between gap-3 border-b border-dashed border-conduril-border py-1.5 text-[13px] last:border-0">
          <span className="text-slate-600">{artigoLabel(a)}</span>
          <span className="whitespace-nowrap tabular-nums">{formatEur(a.valor)}</span>
        </div>
      ))}
      {canEdit && (
        <div className="flex gap-2 pt-2">
          <Button size="sm" variant="secondary" onClick={() => onEdit(g)}>
            {t('common.edit')}
          </Button>
          <Button size="sm" variant="danger" onClick={() => onDelete(g)}>
            {t('common.delete')}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-2xl border border-conduril-border bg-white shadow-card md:block">
        <table className="w-full border-collapse text-sm">
          <thead className="border-b border-conduril-border bg-conduril-surface text-xs uppercase tracking-wide text-conduril-muted">
            <tr>
              <Th label={t('guias.date')} k="data" />
              <Th label={t('guias.obra')} k="obra" />
              <Th label={t('guias.guiaFamily')} k="numero" />
              <Th label={t('guias.value')} k="total" right />
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {pageItems.map((g) => {
              const isOpen = open.has(g.id);
              return (
                <Fragment key={g.id}>
                  <tr
                    onClick={() => toggleOpen(g.id)}
                    className={`cursor-pointer border-b border-conduril-border/70 transition hover:bg-conduril-tint/40 ${
                      isOpen ? 'bg-conduril-tint/40' : ''
                    }`}
                  >
                    <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-600">{fmtDate(g.data)}</td>
                    <td className="px-4 py-3 font-medium text-conduril-ink">{g.obra}</td>
                    <td className="px-4 py-3 text-slate-600">{g.numero || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums">{formatEur(g.total)}</td>
                    <td className="px-3 text-center text-conduril-muted">{isOpen ? '▾' : '▸'}</td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-conduril-surface/60">
                      <td colSpan={5} className="px-6 py-3">
                        {detail(g)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="flex flex-col gap-2.5 md:hidden">
        {pageItems.map((g) => {
          const isOpen = open.has(g.id);
          return (
            <div key={g.id} className="overflow-hidden rounded-2xl border border-conduril-border bg-white shadow-card">
              <button
                type="button"
                onClick={() => toggleOpen(g.id)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
              >
                <div className="min-w-0">
                  <div className="truncate font-semibold text-conduril-ink">{g.obra}</div>
                  <div className="text-xs text-conduril-muted">
                    {fmtDate(g.data)} · {t('guias.guiaNo', { numero: g.numero || '—' })}
                  </div>
                </div>
                <div className="whitespace-nowrap text-right font-semibold tabular-nums">{formatEur(g.total)}</div>
              </button>
              {isOpen && <div className="border-t border-conduril-border bg-conduril-surface/60 px-4 py-3">{detail(g)}</div>}
            </div>
          );
        })}
      </div>

      <Pagination
        page={currentPage}
        pageCount={pageCount}
        total={sorted.length}
        pageSize={PAGE_SIZE}
        onPage={setPage}
      />
    </div>
  );
}
